import React from 'react';
import { ChatSearchResult } from '../../services/apiService';
import { formatInstant } from '../helpers/dataHelper';

interface SearchResultItemProps {
  result: ChatSearchResult;
  query: string;
  onClick: () => void;
}

const SearchResultItem: React.FC<SearchResultItemProps> = ({ result, query, onClick }) => {
  const highlightText = (text: string, highlights: Array<{ start: number; end: number }>) => {
    if (!highlights || highlights.length === 0) {
      return text;
    }

    let highlightedText = '';
    let lastIndex = 0;

    // Sort highlights by start position
    const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);

    sortedHighlights.forEach((highlight, index) => {
      // Add text before highlight
      if (highlight.start > lastIndex) {
        highlightedText += text.slice(lastIndex, highlight.start);
      }

      // Add highlighted text
      highlightedText += `<mark class="bg-yellow-200 px-1 rounded">${text.slice(highlight.start, highlight.end)}</mark>`;

      lastIndex = highlight.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      highlightedText += text.slice(lastIndex);
    }

    return highlightedText;
  };

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3 gap-4 min-w-0">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate min-w-0">
            {result.chat_title}
          </h3>
          <p className="text-sm text-gray-500">
            Chat ID: {result.chat_id}
          </p>
        </div>
        <div className="text-sm text-gray-400">
          {formatInstant(result.message.created_at)}
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            result.message.role === 'user' 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {result.message.role}
          </span>
        </div>
        
        <div 
          className="text-gray-700 leading-relaxed whitespace-pre-line "
          dangerouslySetInnerHTML={{
            __html: highlightText(result.message.content, result.highlights)
          }}
        />
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>Message ID: {result.message.id}</span>
        <button className="text-blue-600 hover:text-blue-800 font-medium">
          View Chat →
        </button>
      </div>
    </div>
  );
};

export default SearchResultItem;
